import { DatePicker, Textarea, Input } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import { Controller, UseFormReturn } from 'react-hook-form';
import FoodAutocomplete from '../FoodAutocomplete';
import StarRating from '../StarRating/StarRating';
import { getLocalTimeZone, now } from '@internationalized/date';
import { ZonedDateTime } from '@internationalized/date';
import PlaceAutocomplete from '../PlaceAutocomplete';
import { FoodResponseDto } from '@bitecritic-api/src/foods/dto/food.response.dto';
import { ReviewCreateDTO } from '@bitecritic-api/src/reviews/dto/review.dto';
import ReviewLanguageSelector from '../ReviewLanguageSelector';
import { ImageResponseDto } from '@bitecritic-api/src/images/dto/image.response.dto';
import { GooglePlace } from '@bitecritic-api/src/places/places.controller';

export type ReviewFormData = Omit<ReviewCreateDTO, 'visitedAt'> & {
  visitedAt: ZonedDateTime;
};

interface ReviewFormProps {
  form: UseFormReturn<ReviewFormData>;
  reviewType: ReviewFormData['type'];
  isSubmitting?: boolean;
  initialFood?: FoodResponseDto | null;
  initialPlace?: GooglePlace | null;
  languageCode: string;
  setLanguageCode: (languageCode: string) => void;
  images: ImageResponseDto[];
  setImages: (images: ImageResponseDto[]) => void;
  step: number;
  onFoodSelect: (food: FoodResponseDto | null) => void;
  onPlaceSelect: (place: GooglePlace | null) => void;
}

export default function ReviewForm({
  form,
  isSubmitting = false,
  initialFood,
  initialPlace,
  languageCode,
  setLanguageCode,
  step,
  onFoodSelect,
  onPlaceSelect,
}: ReviewFormProps) {
  const { t } = useTranslation();
  const { control, formState: { errors } } = form;

  const renderFieldError = (fieldName: keyof ReviewFormData) => {
    const error = errors[fieldName];
    if (error?.message) {
      return <span className="text-danger text-sm">{t(error.message)}</span>;
    }
    return null;
  };

  return (
    <div className="flex items-stretch flex-col h-full max-w-[1000px]">
      <div className="flex-1 mb-4 p-2">
        {/* Step 2: Food Details */}
        <div className="flex flex-col gap-4" style={{ display: step === 2 ? 'flex' : 'none' }}>
          <div>
            <Controller
              name="foodId"
              control={control}
              render={({ field }) => (
                <FoodAutocomplete
                  onChange={(food) => {
                    field.onChange(food?.id);
                    onFoodSelect(food || null);
                  }}
                  value={field.value}
                  initialFood={initialFood}
                  aria-label={t('what-did-you-eat')}
                />
              )}
            />
            {renderFieldError('foodId')}
          </div>
          <div className="pl-2">
            <Controller
              name="overallRating"
              control={control}
              defaultValue={5}
              render={({ field }) => (
                <div className="flex flex-col">
                  <div className="text-sm font-medium text-primary-200">
                    {t('rating-explanation')}
                  </div>
                  <StarRating
                    rating={field.value || 5}
                    isEditable={!isSubmitting}
                    onChange={(value) => field.onChange(value)}
                    aria-label={t('rating')}
                  />
                </div>
              )}
            />
            {renderFieldError('overallRating')}
          </div>
          <div>
            <Controller
              name="reviewText"
              control={control}
              render={({ field }) => (
                <Textarea
                  defaultValue={field.value}
                  label={String(t('review.label'))}
                  placeholder={String(t('write-your-review'))}
                  variant="bordered"
                  minRows={4}
                  maxRows={6}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  isDisabled={isSubmitting}
                  classNames={{
                    input: "text-foreground",
                    label: "text-foreground-200"
                  }}
                />
              )}
            />
            {renderFieldError('reviewText')}
          </div>
          <div>
            <Controller
              name="cost"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field }) => (
                <Input
                  type="number"
                  label={t('cost')}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  variant="bordered"
                  endContent={
                    <div className="pointer-events-none flex items-center">
                      <span className="text-default-400 text-small">
                        {t('localeCurrency')}
                      </span>
                    </div>
                  }
                  onChange={(value) => field.onChange(parseFloat(value.target.value))}
                  isDisabled={isSubmitting}
                />
              )}
            />
            {renderFieldError('cost')}
          </div>
          <ReviewLanguageSelector
            value={languageCode}
            onChange={setLanguageCode}
          />
        </div>

        {/* Step 3: Place Selection */}
        <div className="flex flex-col gap-4" style={{ display: step === 3 ? 'flex' : 'none' }}>
          <div>
            <Controller
              name="googlePlaceId"
              control={control}
              render={({ field }) => (
                <PlaceAutocomplete
                  value={initialPlace?.id}
                  onChange={(place) => {
                    field.onChange(place?.id || '');
                    onPlaceSelect(place || null);
                  }}
                  isDisabled={isSubmitting}
                  variant="review"
                  initialPlace={initialPlace}
                />
              )}
            />
            {renderFieldError('googlePlaceId')}
          </div>
          <div>
            <Controller
              name="visitedAt"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label={t('visited-at')}
                  variant="bordered"
                  granularity='minute'
                  hideTimeZone={true}
                  hourCycle={24}
                  defaultValue={now(getLocalTimeZone())}
                  onChange={(value) => {
                    if (value) {
                      field.onChange(value);
                    }
                  }}
                  classNames={{
                    base: "w-full",
                    input: "text-small",
                  }}
                  isDisabled={isSubmitting}
                />
              )}
            />
            {renderFieldError('visitedAt')}
          </div>
        </div>
      </div>
    </div>
  );
}
